import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { del } from "@/utils/api-handler";
import { toast } from "sonner";
import { DeleteConfirmationDialog } from "@/components/shared/delete-dialog";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PermissionGate } from "@/components/shared/permission-gate";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import { MoreHorizontal, Trash2 } from "lucide-react";

interface ActionCellProps {
    row: { original: DataSource };
}

export const DatasourceActionCell: React.FC<ActionCellProps> = ({ row }) => {
    const queryClient = useQueryClient();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const deleteMutation = useProtectedMutation({
        mutationFn: (id: string) => {
            return del(`/api/documents/datasources/${id}`);
        },
        requiredPermissions: ["DELETE_DATA_SOURCE"],
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["datasources"] });
            queryClient.invalidateQueries({ queryKey: ["documents-files"] });
            toast.success("Data source deleted successfully", {
                description: "The data source has been removed.",
            });
            setDeleteDialogOpen(false);
        },
        onError: (error: any) => {
            toast.error("Failed to delete data source", {
                description:
                    error.response?.data?.message || "An error occurred while deleting",
            });
            setDeleteDialogOpen(false);
        },
    });

    const handleDelete = async () => {
        try {
            await deleteMutation.mutateAsync(row.original.id);
        } catch (error) {
            console.error("Delete operation failed:", error);
        }
    };

    return (
        <div className="flex items-center justify-end">
            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={handleDelete}
                isLoading={deleteMutation.isPending}
                itemName={row.original.name}
                itemType="data source"
            />
            <div className="flex flex-col items-end">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[150px] py-2">
                        <PermissionGate requiredPermissions={["DELETE_DATA_SOURCE"]}>
                            <DropdownMenuItem
                                className="text-destructive focus:text-destructive cursor-pointer"
                                onClick={() => setDeleteDialogOpen(true)}
                                disabled={deleteMutation.isPending}
                            >
                                <Trash2 className="mr-2 h-4 w-4" />
                                {deleteMutation.isPending ? "Deleting..." : "Delete"}
                            </DropdownMenuItem>
                        </PermissionGate>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
};

